/* industryTools.js — Rule-based industry → tool mapping for smart suggestions */
import { getAppLogo } from './usedApps';

// Industry keywords → recommended tools
const INDUSTRY_TOOL_MAP = {
  trades: {
    label: 'Trades & Field Services',
    keywords: ['plumb', 'electric', 'hvac', 'heating', 'locksmith', 'roofing', 'build', 'construct', 'handyman', 'landscap', 'garden', 'fenc', 'paint', 'decorator', 'plaster', 'tiling', 'carpent', 'joiner', 'drain', 'guttering', 'scaffold', 'demolition', 'solar', 'insulation'],
    tools: ['Jobber', 'ServiceM8', 'Xero', 'QuickBooks', 'Google Calendar'],
  },
  cleaning: {
    label: 'Cleaning & Maintenance',
    keywords: ['clean', 'maid', 'janitor', 'pressure wash', 'window clean', 'carpet', 'upholster', 'hygiene', 'saniti'],
    tools: ['Jobber', 'Google Calendar', 'Xero', 'Mailchimp'],
  },
  agency: {
    label: 'Agencies & Consultants',
    keywords: ['agency', 'consult', 'market', 'digital', 'seo', 'design', 'creative', 'media', 'brand', 'advertis', 'pr ', 'public relation', 'recruit', 'talent', 'headhunt'],
    tools: ['HubSpot', 'Pipedrive', 'Mailchimp', 'Google Drive'],
  },
  health: {
    label: 'Health & Wellness',
    keywords: ['physio', 'therap', 'clinic', 'dental', 'chiro', 'osteo', 'massage', 'salon', 'barber', 'beauty', 'spa', 'fitness', 'gym', 'yoga', 'pilat', 'personal train', 'nutrition', 'diet'],
    tools: ['Calendly', 'Google Calendar', 'Mailchimp', 'Stripe'],
  },
  property: {
    label: 'Property & Real Estate',
    keywords: ['property', 'real estate', 'estate agent', 'lettings', 'rental', 'landlord', 'tenant', 'manag'],
    tools: ['HubSpot', 'Xero', 'Google Calendar', 'PandaDoc'],
  },
  auto: {
    label: 'Automotive',
    keywords: ['auto', 'mechanic', 'garage', 'mot ', 'car ', 'vehicle', 'tyre', 'tire', 'body shop', 'windscreen', 'valet'],
    tools: ['Jobber', 'Xero', 'Google Calendar', 'Stripe'],
  },
  food: {
    label: 'Food & Hospitality',
    keywords: ['restaurant', 'cafe', 'cater', 'food', 'baker', 'chef', 'hotel', 'pub', 'bar ', 'hospit'],
    tools: ['Google Calendar', 'Stripe', 'Mailchimp', 'Google Sheets'],
  },
  general: {
    label: 'General SMB',
    keywords: [],
    tools: ['Gmail', 'Google Calendar', 'Stripe', 'Google Sheets'],
  },
};

/**
 * Infer industry from URL or business description using keyword matching.
 * @param {string} input — URL, business name, or description
 * @returns {{ industry: string, label: string, tools: string[] }}
 */
export function inferIndustry(input) {
  if (!input) return { industry: 'general', ...INDUSTRY_TOOL_MAP.general };

  const text = input.toLowerCase();
  let bestMatch = null;
  let bestScore = 0;

  for (const [industry, config] of Object.entries(INDUSTRY_TOOL_MAP)) {
    if (industry === 'general') continue;
    let score = 0;
    for (const keyword of config.keywords) {
      if (text.includes(keyword)) score++;
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = industry;
    }
  }

  if (bestMatch && bestScore > 0) {
    return { industry: bestMatch, ...INDUSTRY_TOOL_MAP[bestMatch] };
  }
  return { industry: 'general', ...INDUSTRY_TOOL_MAP.general };
}

/**
 * Get recommended tools for a specific industry.
 * @param {string} industry — industry key
 * @returns {{ label: string, tools: string[] }}
 */
export function getIndustryTools(industry) {
  return INDUSTRY_TOOL_MAP[industry] || INDUSTRY_TOOL_MAP.general;
}

/**
 * Get tool with logo info for display.
 * @param {string} toolName
 * @returns {{ name: string, logo: string }}
 */
export function getToolWithLogo(toolName) {
  return { name: toolName, logo: getAppLogo(toolName) };
}

export default INDUSTRY_TOOL_MAP;
